import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Download, Share, X } from "lucide-react";

// Home-page prompt to install the PWA (Add to Home Screen) on iOS/Android.
// Hides itself when already installed or dismissed.
export function InstallBanner() {
  const [deferred, setDeferred] = useState(null);
  const [hidden, setHidden] = useState(false);
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    setStandalone(isStandalone);
    if (localStorage.getItem("psl_install_dismissed") === "1") setHidden(true);
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => setStandalone(true);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (standalone || hidden) return null;
  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent) && !window.MSStream;

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  };
  const dismiss = () => { setHidden(true); localStorage.setItem("psl_install_dismissed", "1"); };

  return (
    <div
      data-testid="install-banner"
      className="relative rounded-sm text-cream p-4 flex items-center gap-3 sm:gap-4"
      style={{ backgroundColor: "#E40119" }}
    >
      <button onClick={dismiss} aria-label="Dismiss" data-testid="install-dismiss"
        className="absolute top-2 right-2 text-cream/70 hover:text-cream transition-colors">
        <X size={16} />
      </button>
      <img src="/icon-192.png" alt="PSL" className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl shrink-0" />
      <div className="min-w-0 flex-1 pr-5">
        <div className="font-head font-black text-base sm:text-lg leading-tight">Install the PSL app</div>
        <p className="text-xs sm:text-sm text-cream/90 mt-0.5">
          Add it to your home screen — full-screen and free. No App Store or Play Store needed.
        </p>
        {isIOS && (
          <p className="text-[11px] text-cream/85 mt-1 flex items-center gap-1">
            <Share size={12} /> Tap <b>Share</b>, then <b>Add to Home Screen</b>.
          </p>
        )}
      </div>
      {deferred ? (
        <button onClick={install} data-testid="install-now-btn"
          className="shrink-0 px-4 py-2.5 bg-gold text-ink font-black rounded-sm flex items-center gap-1.5 hover:bg-cream transition-colors">
          <Download size={16} /> Install
        </button>
      ) : (
        <Link to="/install" data-testid="install-how-btn"
          className="shrink-0 px-4 py-2.5 bg-gold text-ink font-black rounded-sm flex items-center gap-1.5 hover:bg-cream transition-colors">
          <Download size={16} /> {isIOS ? "How to" : "Install"}
        </Link>
      )}
    </div>
  );
}
